import React from 'react';
import FavoriteList from '../../features/media/FavoriteList';

// Infer FavoriteList props from the component, then strip `onJump`
type BaseProps = Omit<React.ComponentProps<typeof FavoriteList>, 'onJump'>;

export type FavoriteListAdapterProps = BaseProps & {
  /** The fileId context to jump within (e.g., "product123:video:main:hash") */
  fileId?: string | null;
  /** Upstream handler that expects fileId + seconds */
  onJumpToTimestamp: (fileId: string, seconds: number | string) => void;
};

/**
 * FavoriteListAdapter
 * Wraps FavoriteList to adapt its `onJump(seconds)` to your upstream
 * `(fileId, seconds)` signature.
 *
 * If `fileId` is not provided, `onJump` will no-op.
 */
export default function FavoriteListAdapter(props: FavoriteListAdapterProps) {
  const { fileId, onJumpToTimestamp, ...rest } = props;
  const safeFileId = fileId && String(fileId).trim();

  const handleJump = React.useCallback(
    (seconds: number) => {
      if (!safeFileId) return; // no-op until you provide a fileId context
      onJumpToTimestamp(safeFileId, seconds);
    },
    [safeFileId, onJumpToTimestamp]
  );

  return <FavoriteList {...(rest as any)} onJump={handleJump} />;
}
